import { notification } from "antd";

import { AppDispatch, RootState } from "@/store";
import { serverSocket } from "@/store/reducers/serverConnection/asyncActionCreators";
import { IExaminationDataValue } from "@/models/ExaminationDataValue";
import {
  addExaminationData,
  clearExaminationData,
  clearPlcErrors,
  logPlcError,
  setActiveExamination,
  setCorrection,
  setCurrentDataValue,
  setCurrentExaminationData,
  setInitExaminationData,
  setM15,
  setPlcError,
  setUnrecordedExaminationError,
} from "@/store/reducers/currentData/currentDataSlice";
import { IExamination } from "@/models/Examination";
import { ICorrection } from "@/models/Correction";
import { CreateCorrectionPayload } from "@/api/types";
import normalizeExaminationValueDate from "@/utils/normalizeExaminationValueDate";

const getLastExaminationTime = (getState: () => RootState) => {
  const last = getState().currentDataReducer.examinationData.at(-1);
  return last ? last.Time : null;
};

const requestExaminationData = (time: string | null, dispatch: AppDispatch) => {
  serverSocket.emit(
    "SERVER:get_examination_data",
    time,
    (response: IExaminationDataValue[]) => {
      if (response.length) {
        dispatch(
          addExaminationData(response.map(normalizeExaminationValueDate))
        );
      }
    }
  );
};

let listenExaminationDataInterval: ReturnType<typeof setInterval>;
export const listenExaminationData =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      clearInterval(listenExaminationDataInterval);
      listenExaminationDataInterval = setInterval(() => {
        if (getState().currentDataReducer.m15) {
          const time = getLastExaminationTime(getState);
          requestExaminationData(time, dispatch);
        }
      }, 1000);
    } catch (e: any) {}
  };

export const listenCurrentData = () => async (dispatch: AppDispatch) => {
  try {
    setTimeout(() => {
      serverSocket.off("SERVER:current_data");
      serverSocket.on(
        "SERVER:current_data",
        ({ data }: { data: IExaminationDataValue }) => {
          dispatch(setCurrentDataValue(data));
        }
      );
    });
  } catch (e: any) {}
};

export const clearCurrentDataListener = () => async () => {
  try {
    serverSocket.off("SERVER:current_data");
  } catch (e: any) {}
};

export const listenCurrentExaminationData =
  () => async (dispatch: AppDispatch) => {
    try {
      serverSocket.off("SERVER:current_examination_data");
      serverSocket.on(
        "SERVER:current_examination_data",
        ({ data }: { data: IExaminationDataValue }) => {
          dispatch(
            setCurrentExaminationData(normalizeExaminationValueDate(data))
          );
        }
      );
    } catch (e: any) {}
  };

export const stopExaminationDataListener = () => () => {
  serverSocket.off("SERVER:examination_data");
};

export const listenM15 =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      serverSocket.off("SERVER:m15");
      serverSocket.on("SERVER:m15", ({ data: marker }: { data: boolean }) => {
        if (marker) {
          dispatch(clearExaminationData());
          dispatch(clearPlcErrors());
        } else {
          const activeExaminationId =
            getState().currentDataReducer.activeExamination;
          if (activeExaminationId) {
            dispatch(setActiveExamination(null));
          }
        }
        dispatch(setM15(marker));
      });
    } catch (e: any) {}
  };

export const listenPlcError = () => (dispatch: AppDispatch) => {
  try {
    serverSocket.off("SERVER:plc_error");
    serverSocket.on(
      "SERVER:plc_error",
      ({
        data: error,
      }: {
        data: { type: "major" | "minor"; message: string };
      }) => {
        if (error.type === "major") {
          dispatch(setPlcError(error.message));
        }
        dispatch(logPlcError(error.message));
      }
    );
  } catch (e: any) {}
};

export const listenAllExaminationData = () => (dispatch: AppDispatch) => {
  serverSocket.off("SERVER:all_examination_data");
  serverSocket.on(
    "SERVER:all_examination_data",
    ({ data }: { data: IExaminationDataValue[] }) => {
      dispatch(setInitExaminationData(data.map(normalizeExaminationValueDate)));
    }
  );
};

export const activeExaminationListener = () => (dispatch: AppDispatch) => {
  try {
    serverSocket.off("SERVER:chosen_examination");
    serverSocket.on(
      "SERVER:chosen_examination",
      ({ data: examination }: { data: IExamination }) => {
        dispatch(setActiveExamination(examination));
      }
    );
  } catch (e: any) {}
};

export const newCorrectionListener = () => (dispatch: AppDispatch) => {
  try {
    serverSocket.off("SERVER:new_correction");
    serverSocket.on(
      "SERVER:new_correction",
      ({ data: correction }: { data: ICorrection }) => {
        dispatch(setCorrection(correction));
      }
    );
  } catch (e: any) {}
};

export const unrecordedExaminationListener = () => (dispatch: AppDispatch) => {
  try {
    serverSocket.off("SERVER:unrecorded_examination");
    serverSocket.on(
      "SERVER:unrecorded_examination",
      ({ data: isExamination }: { data: boolean }) => {
        dispatch(setUnrecordedExaminationError(isExamination));
      }
    );
  } catch (e: any) {}
};

export const deleteUnrecordedExamination = () => {
  serverSocket.emit("SERVER:delete_unrecorded_examination");
};

export const sendCorrection =
  (correctionData: CreateCorrectionPayload) => () => {
    serverSocket.emit(
      "SERVER:create_correction",
      {
        data: correctionData,
      },
      (error: string) => {
        notification.open({
          type: "error",
          message: error,
        });
      }
    );
  };

export const activateExamination = (examId: number | null) => () => {
  try {
    if (serverSocket.connected) {
      serverSocket.volatile.emit(
        "SERVER:chosen_examination",
        {
          data: examId,
        },
        (error: string) => {
          notification.open({
            type: "error",
            message: error,
          });
        }
      );
    }
  } catch (e: any) {}
};
